import { useCallback, useEffect, useState, type CSSProperties, type FormEvent } from 'react'
import { Button, Card, DataTable, Input, Modal } from '@w7/ui'
import { bookmakerApi, bmErrorMessage, unwrapBmList } from '@/api/bookmaker'
import { BmPage } from './BmPage'

type CategoryRow = {
  eventId: string
  eventName: string
  createdAt: string
}

type StakeDetail = { level: string; stake: number }
type WinLoseAmt = { win: number; lose: number }

type PortalTurnover = {
  portalName: string
  event: string
  isData: boolean
  stakes?: { total: number; detail: StakeDetail[] }
  winLose?: { total: number; detail: Record<string, WinLoseAmt> }
}

function paginationMeta(data: unknown): { totalItems: number; itemsPerPage: number } {
  const empty = { totalItems: 0, itemsPerPage: 10 }
  if (!data || typeof data !== 'object') return empty
  const o = data as Record<string, unknown>
  const wrap =
    o.data && typeof o.data === 'object' && !Array.isArray(o.data)
      ? (o.data as Record<string, unknown>)
      : o
  return {
    totalItems: typeof wrap.totalItems === 'number' ? wrap.totalItems : 0,
    itemsPerPage: typeof wrap.itemsPerPage === 'number' ? wrap.itemsPerPage : 10,
  }
}

function formatDate(raw: unknown): string {
  if (raw == null || raw === '') return '—'
  const d = new Date(String(raw))
  if (Number.isNaN(d.getTime())) return String(raw)
  return d.toLocaleDateString()
}

function moneyTone(n: number): CSSProperties {
  if (n > 0) return { color: 'var(--w7-success, #1b7a3d)' }
  if (n < 0) return { color: 'var(--w7-danger, #c62828)' }
  return {}
}

function formatNum(n: number): string {
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 })
}

function isEmpty(v: unknown): boolean {
  if (v == null) return true
  if (Array.isArray(v)) return v.length === 0
  if (typeof v === 'object') return Object.keys(v as object).length === 0
  return false
}

/** Lab TurnoverCtrl.getTurnOverForMarket mapping — Book Maker level only. */
function mapPortalTurnover(raw: unknown, eventName: string): PortalTurnover[] {
  let list: unknown[] = []
  if (Array.isArray(raw)) list = raw
  else if (raw && typeof raw === 'object') {
    const o = raw as Record<string, unknown>
    if (Array.isArray(o.data)) list = o.data
    else if (o.data && typeof o.data === 'object' && Array.isArray((o.data as Record<string, unknown>).result)) {
      list = (o.data as Record<string, unknown>).result as unknown[]
    } else if (Array.isArray(o.result)) list = o.result
  }

  return list.map((item) => {
    const single = (item && typeof item === 'object' ? item : {}) as Record<string, unknown>
    const portalName = String(single.portalName ?? '—')
    const stakesArr = Array.isArray(single.stakes) ? single.stakes : []
    if (isEmpty(stakesArr)) {
      return { portalName, event: eventName, isData: false }
    }

    const stakes0 = (stakesArr[0] && typeof stakesArr[0] === 'object' ? stakesArr[0] : {}) as Record<
      string,
      unknown
    >
    const detailRaw = Array.isArray(stakes0.detail) ? stakes0.detail : []
    const bmStakes = detailRaw
      .filter((d) => d && typeof d === 'object' && (d as { level?: string }).level === 'Book Maker')
      .map((d) => {
        const row = d as { level?: string; stake?: number }
        return { level: String(row.level ?? ''), stake: Number(row.stake ?? 0) }
      })

    const stakesTotal = bmStakes.reduce((s, r) => s + r.stake, 0)

    const winLoseArr = Array.isArray(single.winLose) ? single.winLose : []
    const winLose0 = (winLoseArr[0] && typeof winLoseArr[0] === 'object' ? winLoseArr[0] : {}) as Record<
      string,
      unknown
    >
    const wlDetailRaw = Array.isArray(winLose0.detail) ? winLose0.detail : []
    const winLoseDetail: Record<string, WinLoseAmt> = {}
    let winLoseTotal = 0
    let isData = false

    for (const market of wlDetailRaw) {
      if (!market || typeof market !== 'object') continue
      const m = market as { level?: string; type?: string; amount?: number }
      if (m.level !== 'Book Maker') continue
      isData = true
      const level = String(m.level)
      if (!winLoseDetail[level]) winLoseDetail[level] = { win: 0, lose: 0 }
      const amount = Number(m.amount ?? 0)
      if (m.type === 'Win') winLoseDetail[level].win = amount
      else if (m.type === 'Lose') winLoseDetail[level].lose = amount
      winLoseTotal += amount
    }

    return {
      portalName,
      event: eventName,
      isData: isData || bmStakes.length > 0,
      stakes: { total: stakesTotal, detail: bmStakes },
      winLose: { total: winLoseTotal, detail: winLoseDetail },
    }
  })
}

export function BmTurnoverPage() {
  const [rows, setRows] = useState<CategoryRow[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [detailBusy, setDetailBusy] = useState(false)
  const [page, setPage] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [search, setSearch] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [allTurnover, setAllTurnover] = useState<PortalTurnover[]>([])

  const load = useCallback(async (pageNo: number, eventName: string) => {
    setBusy(true)
    setError(null)
    try {
      const body: Record<string, unknown> = { page: pageNo }
      if (eventName.trim()) body.eventName = eventName.trim()
      const data = await bookmakerApi.getCategoriesForTurnover(body)
      const list = unwrapBmList(data)
      const meta = paginationMeta(data)
      setRows(
        list.map((r) => ({
          eventId: String(r.eventId ?? r._id ?? r.id ?? ''),
          eventName: String(r.eventName ?? r.name ?? '—'),
          createdAt: String(r.createdAt ?? r.date ?? ''),
        })),
      )
      setTotalItems(meta.totalItems || list.length)
      setItemsPerPage(meta.itemsPerPage || 10)
      setPage(pageNo)
    } catch (e) {
      setError(bmErrorMessage(e))
      setRows([])
    } finally {
      setBusy(false)
    }
  }, [])

  useEffect(() => {
    void load(1, '')
  }, [load])

  const onSearch = (e: FormEvent) => {
    e.preventDefault()
    setAppliedSearch(search)
    void load(1, search)
  }

  const openDetails = async (eventName: string, eventId: string) => {
    setDetailBusy(true)
    setError(null)
    try {
      // Lab TurnoverCtrl hardcodes level / memberId / isBookmaker
      const data = await bookmakerApi.getTurnOverForMarket({
        eventName,
        eventId,
        level: 'CompanyMaster',
        memberId: '5eccb43da468913000e83ebb',
        isBookmaker: true,
      })
      setAllTurnover(mapPortalTurnover(data, eventName))
      setModalOpen(true)
    } catch (e) {
      setError(bmErrorMessage(e))
    } finally {
      setDetailBusy(false)
    }
  }

  const totalPages = Math.max(1, Math.ceil((totalItems || rows.length) / (itemsPerPage || 10)))
  const eventTitle = allTurnover[0]?.event ?? 'Turnover'

  return (
    <BmPage title="Turn Over" description="Lab /turnover" error={error}>
      <Card>
        <form
          onSubmit={onSearch}
          style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}
        >
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search.."
            aria-label="Search events"
            style={{ minWidth: 220 }}
          />
          <Button type="submit" variant="secondary" disabled={busy}>
            Search
          </Button>
        </form>

        <DataTable
          label="Turnover events"
          columns={[
            {
              id: 'sr',
              header: 'Sr. No.',
              cell: (_r, i) => String(itemsPerPage * (page - 1) + (i ?? 0) + 1),
            },
            {
              id: 'event',
              header: 'Event',
              cell: (r: CategoryRow) => r.eventName,
            },
            {
              id: 'date',
              header: 'Date',
              cell: (r: CategoryRow) => formatDate(r.createdAt),
            },
            {
              id: 'action',
              header: 'Action',
              cell: (r: CategoryRow) => (
                <Button
                  variant="secondary"
                  disabled={detailBusy || !r.eventId}
                  onClick={() => void openDetails(r.eventName, r.eventId)}
                >
                  details
                </Button>
              ),
            },
          ]}
          rows={rows}
          loading={busy}
          rowKey={(r) => r.eventId || r.eventName}
        />

        {rows.length > 0 ? (
          <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
            <Button
              variant="secondary"
              disabled={busy || page <= 1}
              onClick={() => void load(page - 1, appliedSearch)}
            >
              Prev
            </Button>
            <span>
              Page {page} / {totalPages}
            </span>
            <Button
              variant="secondary"
              disabled={busy || page >= totalPages}
              onClick={() => void load(page + 1, appliedSearch)}
            >
              Next
            </Button>
          </div>
        ) : null}
      </Card>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={eventTitle}>
        <div style={{ display: 'grid', gap: 16 }}>
          {allTurnover.length === 0 ? (
            <p style={{ margin: 0, textAlign: 'center', opacity: 0.75 }}>No Data Found</p>
          ) : (
            allTurnover.map((portal) =>
              portal.isData ? (
                <div
                  key={portal.portalName}
                  style={{
                    border: '1px solid var(--w7-border-subtle)',
                    borderRadius: 8,
                    padding: 12,
                  }}
                >
                  <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>{portal.portalName}</h3>

                  <div style={{ textAlign: 'center', marginBottom: 16 }}>
                    <h4 style={{ margin: '0 0 6px' }}>Stakes</h4>
                    <div style={moneyTone(portal.stakes?.total ?? 0)}>
                      Total: {formatNum(portal.stakes?.total ?? 0)}
                    </div>
                    <table style={{ width: '100%', marginTop: 8, borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left', padding: 6 }}>Level</th>
                          <th style={{ textAlign: 'right', padding: 6 }}>Stake</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(portal.stakes?.detail ?? []).map((s) => (
                          <tr key={s.level}>
                            <td style={{ padding: 6 }}>{s.level}</td>
                            <td style={{ padding: 6, textAlign: 'right', ...moneyTone(s.stake) }}>
                              {formatNum(s.stake)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div style={{ textAlign: 'center' }}>
                    <h4 style={{ margin: '0 0 6px' }}>Win/Lose</h4>
                    <div style={moneyTone(portal.winLose?.total ?? 0)}>
                      Total: {formatNum(portal.winLose?.total ?? 0)}
                    </div>
                    <table style={{ width: '100%', marginTop: 8, borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left', padding: 6 }}>Level</th>
                          <th style={{ textAlign: 'right', padding: 6 }}>Win</th>
                          <th style={{ textAlign: 'right', padding: 6 }}>Lose</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(portal.winLose?.detail ?? {}).map(([level, amt]) => (
                          <tr key={level}>
                            <td style={{ padding: 6 }}>{level}</td>
                            <td
                              style={{
                                padding: 6,
                                textAlign: 'right',
                                color: 'var(--w7-success, #1b7a3d)',
                              }}
                            >
                              {formatNum(amt.win)}
                            </td>
                            <td
                              style={{
                                padding: 6,
                                textAlign: 'right',
                                color: 'var(--w7-danger, #c62828)',
                              }}
                            >
                              {formatNum(amt.lose)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div key={portal.portalName} style={{ textAlign: 'center' }}>
                  <h3 style={{ margin: '0 0 8px', fontSize: 15 }}>{portal.portalName}</h3>
                  <p style={{ margin: 0, color: 'var(--w7-success, #1b7a3d)' }}>No Data Found</p>
                </div>
              ),
            )
          )}
        </div>
      </Modal>
    </BmPage>
  )
}
